import { computed, reactive, watchEffect } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'

/**
 * Cross-field validation for multiplexed vs nr_of_samples.
 * Shared by IlluminaSequencing, NanoporeSequencing, and AvitSequencing.
 *
 * @param {Object} dv - The reactive detail view object
 * Returns { multiplexingError, isTemplate } — both computed refs.
 */
export function useMultiplexingValidation(dv) {
  const multiplexed = computed(() => {
    return dv.editedRecord['multiplexed']?.value
  })
  const nrOfSamples = computed(() => {
    const raw = dv.editedRecord['nr_of_samples']?.value
    return parseInt(raw, 10)
  })

  const isTemplate = computed(() => {
    return dv.editedRecord['is_template']?.value || false
  })

  const multiplexingError = computed(() => {
    if (isTemplate.value) return null
    const sampleCount = nrOfSamples.value
    if (isNaN(sampleCount)) return null

    if (multiplexed.value && sampleCount <= 1) {
      return 'Multiplexed assays require more than 1 sample'
    }
    if (!multiplexed.value && sampleCount !== 1) {
      return 'Non-multiplexed assays must have exactly 1 sample'
    }
    return null
  })

  return { multiplexingError, isTemplate }
}

/**
 * Bridges cross-field validation errors into vuelidate AND provides
 * a plain object for inline display. These serve two separate consumers:
 *
 * 1. useVuelidate() — registers a child v$ instance so the parent form's
 *    v$.$invalid becomes true when any error is active. This BLOCKS SAVE.
 *    Nothing in this component reads v$ directly; it's pure plumbing.
 *
 * 2. validationErrors — a { key: message } object passed as a prop to
 *    template components (e.g. SequencingDescription) to render inline
 *    red text next to the affected fields. Vuelidate can't do this for us
 *    because these are cross-field errors that don't belong to any single
 *    field's own vuelidate instance.
 *
 * Both read from the same errorRefs source but feed different systems.
 *
 * @param {Object} errorRefs - { key: computedRef } pairs, e.g. { multiplexed: multiplexingError }
 * @returns {{ validationErrors: ComputedRef<Object> }} - errors keyed by field name for inline display
 */
export function useAssayVuelidate(errorRefs) {
  const keys = Object.keys(errorRefs)

  // --- Part 1: Vuelidate integration (blocks save) ---
  // State holds the current error message string; truthy = invalid.
  const state = reactive(Object.fromEntries(keys.map((k) => [k, null])))

  // Validator that fails when the state value (= error message) is truthy.
  // withMessage uses the error string itself as the vuelidate $message.
  const noError = helpers.withMessage(
    ({ $model }) => $model || '',
    (value) => !value,
  )
  const rules = Object.fromEntries(keys.map((k) => [k, { noError }]))

  // Keep reactive state in sync with the source error computeds
  keys.forEach((key) => {
    watchEffect(() => {
      state[key] = errorRefs[key].value
    })
  })

  // Registers a child v$ that the parent form's v$ collects automatically
  useVuelidate(rules, state, { $autoDirty: true })

  // --- Part 2: Inline display (shows red text next to fields) ---
  const validationErrors = computed(() => {
    const errors = {}
    keys.forEach((key) => {
      const error = errorRefs[key].value
      if (error) errors[key] = error
    })
    return errors
  })

  return { validationErrors }
}
